/**
 * InfoSection - FAQ & Information Display
 *
 * Shows hub information, FAQs, rules, and other static content.
 */

import { useState } from "react";
import { FaInfoCircle, FaChevronDown, FaChevronUp, FaExternalLinkAlt } from "react-icons/fa";
import { HubResponse } from "@/hooks/hub/useGetHub";
import "./InfoSection.scss";

interface InfoSectionProps {
    hub: HubResponse;
}

interface FAQItem {
    question: string;
    answer: string;
}

// Default FAQs - can be extended per-hub
const DEFAULT_FAQS: FAQItem[] = [
    {
        question: "How do I earn XP?",
        answer: "Complete missions, participate in challenges, and engage with the community to earn XP. Your XP contributes to your level and unlocks rewards.",
    },
    {
        question: "How do I join a squad?",
        answer: "Navigate to the Squads section to browse available squads or create your own. Joining a squad lets you compete together and earn bonus rewards.",
    },
    {
        question: "What are retail campaigns?",
        answer: "Retail campaigns let you unlock exclusive in-game rewards by purchasing products from participating retailers. Verify your purchase to claim rewards.",
    },
    {
        question: "How do I participate in tournaments?",
        answer: "Check the Challenges section for upcoming tournaments. Register before the deadline and show up ready to compete!",
    },
];

export const InfoSection = ({ hub }: InfoSectionProps) => {
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

    const toggleFaq = (index: number) => {
        setExpandedFaq(expandedFaq === index ? null : index);
    };

    return (
        <section className="section info-section">
            <div className="section__header">
                <h2>
                    <FaInfoCircle />
                    Information
                </h2>
                <p>Everything you need to know about {hub.name}</p>
            </div>

            {/* About Section */}
            {hub.description &&
                <div className="info-section__about">
                    <h3>About</h3>
                    <p>{hub.description}</p>
                </div>
            }

            {/* FAQ Section */}
            <div className="info-section__faq">
                <h3>Frequently Asked Questions</h3>
                <div className="info-section__faq-list">
                    {DEFAULT_FAQS.map((faq, index) =>
                        <div
                            key={index}
                            className={`info-section__faq-item ${
                                expandedFaq === index ? "info-section__faq-item--expanded" : ""
                            }`}
                        >
                            <button
                                className="info-section__faq-question"
                                onClick={() => toggleFaq(index)}
                            >
                                <span>{faq.question}</span>
                                {expandedFaq === index
                                    ? <FaChevronUp />
                                    : <FaChevronDown />
                                }
                            </button>
                            {expandedFaq === index &&
                                <div className="info-section__faq-answer">
                                    <p>{faq.answer}</p>
                                </div>
                            }
                        </div>)}
                </div>
            </div>

            {/* Links Section */}
            <div className="info-section__links">
                <h3>Helpful Links</h3>
                <div className="info-section__links-grid">
                    <a href="https://devourgo.io/terms" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                        <FaExternalLinkAlt size={12} />
                    </a>
                    <a href="https://devourgo.io/privacy" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                        <FaExternalLinkAlt size={12} />
                    </a>
                    <a href="https://discord.gg/devourplay" target="_blank" rel="noopener noreferrer">
                        Join Discord
                        <FaExternalLinkAlt size={12} />
                    </a>
                    <a href="https://devourgo.io/support" target="_blank" rel="noopener noreferrer">
                        Support
                        <FaExternalLinkAlt size={12} />
                    </a>
                </div>
            </div>
        </section>
    );
};
